import { InvoiceStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { useInvoiceById } from "Order/Modules/Invoice/Api/Query/useInvoiceByIdQuery";
import { InvoiceActions } from "Order/Modules/Invoice/Components/InvoiceActions";
import { Loading } from "Shared/Components/Loading/Loading";
import {
	Box,
	Chip,
	type ChipProps,
	Divider,
	Grid2,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";

type InvoiceDetailWidgetProps = {
	invoiceId: number;
	showRegenerateInvoiceButton?: boolean;
};

const invoiceResource = Resources.base.invoice.detail;

export function InvoiceDetailWidget({
	invoiceId,
	showRegenerateInvoiceButton,
}: InvoiceDetailWidgetProps) {
	const { t } = useResource();
	const { data: invoice, isLoading } = useInvoiceById(invoiceId);
	const { formatDate } = useDateFormat();

	const locale = invoice?.market?.country;
	const currency = invoice?.market?.currency;

	const invoiceStatusColor: Record<InvoiceStatus, ChipProps["color"]> = {
		[InvoiceStatus.Pending]: "default",
		[InvoiceStatus.Paid]: "success",
		[InvoiceStatus.Cancelled]: "error",
	};

	if (isLoading)
		return (
			<Stack alignItems="center">
				<Loading size={35} />
			</Stack>
		);

	return (
		invoice && (
			<Stack gap={2}>
				<Stack
					direction="row"
					justifyContent="space-between"
					spacing={2}
					rowGap={{ xs: 2, sm: 0 }}
					flexWrap={{ xs: "wrap", sm: "nowrap" }}
				>
					<Stack
						gap={1}
						flexDirection="row"
						alignItems="flex-start"
						justifyContent={{ xs: "space-between", sm: "flex-start" }}
					>
						<Typography variant="h5">#{invoice.invoiceNumber}</Typography>
						<Stack gap={1} flexDirection="row" flexWrap="wrap">
							<Box>
								<Chip
									size="small"
									color="primary"
									variant="outlined"
									label={`${t(invoiceResource.issued)} ${formatDate(invoice.dateIssued)}`}
								/>
							</Box>
							<Box>
								<Chip
									size="small"
									color={invoiceStatusColor[invoice.status]}
									variant="outlined"
									label={
										invoice.status === InvoiceStatus.Pending
											? "Unpaid"
											: invoice.status
									}
								/>
							</Box>
							{invoice.datePaid && (
								<Box>
									<Chip
										size="small"
										color="success"
										variant="outlined"
										label={`${t(invoiceResource.paid)} ${formatDate(invoice.datePaid)}`}
									/>
								</Box>
							)}
						</Stack>
					</Stack>

					<Stack
						flexDirection={{ xs: "column", sm: "row" }}
						gap={{ xs: 1, sm: 2 }}
						alignItems={{ xs: "flex-start", sm: "center" }}
						sx={{ ml: "0 !important", mt: { xs: 1, sm: 0 } }}
					>
						{invoice?.order && (
							<InvoiceActions
								invoice={invoice}
								showRegenerateInvoiceButton={showRegenerateInvoiceButton}
							/>
						)}
					</Stack>
				</Stack>
				<Divider />

				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12, sm: 6 }}>
						<Paper variant="outlined">
							<Stack p={3} gap={1}>
								<Typography variant="h6" mb={2}>
									{t(invoiceResource.from)}:
								</Typography>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.bankAccount)}
									</Typography>
									<Typography variant="body1">{invoice.bankAccount}</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.bankName)}
									</Typography>
									<Typography variant="body1">{invoice.bankName}</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.iban)}
									</Typography>
									<Typography variant="body1">{invoice.iban}</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.paymentMethod)}
									</Typography>
									<Typography variant="body1">
										{invoice.paymentMethod}
									</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.variableSymbol)}
									</Typography>
									<Typography variant="body1">
										{invoice.variableSymbol}
									</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.dateIssued)}
									</Typography>
									<Typography variant="body1">
										{invoice.dateIssued && formatDate(invoice.dateIssued)}
									</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.dateDue)}
									</Typography>
									<Typography variant="body1">
										{invoice.dateDue && formatDate(invoice.dateDue)}
									</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.partnerName)}
									</Typography>
									<Typography variant="body1">
										{invoice.supplierName}
									</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.partnerBusinessId)}
									</Typography>
									<Typography variant="body1">
										{invoice.supplierBusinessId}
									</Typography>
								</Stack>

								<Stack flexDirection="row" justifyContent="space-between">
									<Typography variant="body1">
										{t(invoiceResource.partnerAddress)}
									</Typography>
									<Typography variant="body1" textAlign="right">
										{`${invoice.supplierStreet} ${invoice.supplierBuildingNumber}, ${invoice.supplierCity}, ${invoice.supplierPostCode}`}
									</Typography>
								</Stack>
							</Stack>
						</Paper>
					</Grid2>

					<Grid2 size={{ xs: 12, sm: 6 }}>
						<Paper variant="outlined">
							<Stack p={3} gap={1}>
								<Typography variant="h6" mb={2}>
									{t(invoiceResource.customer)}:
								</Typography>
								<Typography variant="body1">{invoice.customerName}</Typography>
								<Typography variant="body1">
									{invoice.customerStreet}&nbsp;{invoice.customerBuildingNumber}
								</Typography>
								<Typography variant="body1">{invoice.customerCity}</Typography>
								<Typography variant="body1">
									{invoice.customerPostCode}
								</Typography>

								{invoice.customerBusinessId && (
									<Stack flexDirection="row" justifyContent="space-between">
										<Typography variant="body1">
											{t(invoiceResource.businessId)}
										</Typography>
										<Typography variant="body1">
											{invoice.customerBusinessId}
										</Typography>
									</Stack>
								)}
								{invoice.customerVatId && (
									<Stack flexDirection="row" justifyContent="space-between">
										<Typography variant="body1">
											{t(invoiceResource.vatId)}
										</Typography>
										<Typography variant="body1">
											{invoice.customerVatId}
										</Typography>
									</Stack>
								)}
							</Stack>
						</Paper>
					</Grid2>
				</Grid2>

				<Paper variant="outlined">
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>
										<Typography variant="h6">
											{t(invoiceResource.name)}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="h6">
											{t(invoiceResource.count)}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="h6">
											{t(invoiceResource.unitPrice)}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="h6">
											{t(invoiceResource.totalAmount)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="h6">
											{t(invoiceResource.vat)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="h6">
											{t(invoiceResource.totalAmountVatIncluded)}
										</Typography>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{invoice?.items?.map((invoiceItem) => (
									<TableRow key={invoiceItem.id}>
										<TableCell>
											<Typography variant="body1">
												{invoiceItem.name}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body1">
												{invoiceItem.count}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body1">
												{formatCurrency(
													locale,
													currency,
													invoiceItem.pricePerUnit,
												)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body1">
												{formatCurrency(
													locale,
													currency,
													invoiceItem.totalPrice,
												)}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body1">
												{formatCurrency(locale, currency, invoiceItem.vat)}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body1">
												{formatCurrency(
													locale,
													currency,
													invoiceItem.totalPriceVat,
												)}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
				<Stack p={3} alignItems="flex-end" bgcolor="primary.light">
					<Stack
						flexDirection="row"
						gap={3}
						justifyContent="space-between"
						width={{ xs: "100%", md: "30%" }}
					>
						<Typography variant="body1">
							{t(invoiceResource.totalAmount)}:
						</Typography>
						<Typography variant="body1">
							{formatCurrency(locale, currency, invoice.totalAmountWithoutVat)}
						</Typography>
					</Stack>
					<Stack
						flexDirection="row"
						gap={3}
						justifyContent="space-between"
						width={{ xs: "100%", md: "30%" }}
					>
						<Typography variant="body1">{t(invoiceResource.vat)}:</Typography>
						<Typography variant="body1">
							{formatCurrency(locale, currency, invoice.totalVatAmount)}
						</Typography>
					</Stack>
					<Stack
						flexDirection="row"
						gap={3}
						justifyContent="space-between"
						width={{ xs: "100%", md: "30%" }}
						mb={3}
					>
						<Typography variant="body1">
							{t(invoiceResource.totalAmountVatIncluded)}:
						</Typography>
						<Typography variant="body1">
							{formatCurrency(locale, currency, invoice.totalAmountWithVat)}
						</Typography>
					</Stack>
					<Stack flexDirection="row" gap={3}>
						<Typography variant="h4">
							{t(invoiceResource.customerDebt)}:
						</Typography>
						<Typography variant="h4">
							{formatCurrency(locale, currency, invoice.customerDebt)}
						</Typography>
					</Stack>
				</Stack>
			</Stack>
		)
	);
}
